/**
 * Writing tablature: a fret typed at the caret, the string it lands on, and two-digit frets.
 *
 * A tab staff is typed as numbers on a string rather than letters on a stave, so this is
 * the fretted half of note entry -- what a digit and the up and down arrows do when the
 * caret is on a staff whose lines are strings.
 *
 * The part that needs saying is the two-digit fret, which is one note written by two
 * keystrokes.
 *
 * A guitar has twenty-four frets and a keyboard has ten digits, so a number above nine has
 * to be typed in two goes: `1` then `2` is the twelfth fret, not the first fret and then
 * the second. Every program that writes tablature works this way, and the run is what makes
 * the top half of the neck reachable from the keyboard at all.
 *
 * The run is decided by the document rather than by a clock. It continues only while the
 * note last written is still the note the previous digit wrote, so anything else at all --
 * moving the caret, changing the value, writing a rest -- ends it. A digit only joins the
 * one before it when the two make a fret that exists: nothing on this instrument is past
 * fret 24, so `3` after `5` is a new note rather than a fret 53 that has to be refused.
 */

import type { Command } from "../commands/types";
import { note as makeNote } from "../document/create";
import type { EventId } from "../document/ids";
import { locateEvent, measureIndexOf, resolveAttributes } from "../document/query";
import { HIGHEST_FRET, pitchAt } from "../document/tab";
import type { StringTuning } from "../document/tab";
import type { Score } from "../document/types";
import { tuningAt } from "./entry-spell";
import { refuse, tieBack, write } from "./entry-write";
import type { EntryResult, EntryState } from "./entry-write";

/** A digit taken as the second of two: the edit to apply, and the fret now written. */
export interface Extended {
  readonly command: Command;
  readonly fret: number;
}

/**
 * Join a digit to the fret just written, or `null` when it cannot be joined.
 *
 * A chord is left alone: its heads are on different strings and there is no reading of "the
 * fret just typed" that covers all of them.
 */
export function extendFret(
  score: Score,
  tuning: readonly StringTuning[],
  eventId: EventId,
  digit: number,
): Extended | null {
  const at = locateEvent(score, eventId);
  if (at === null || at.event.kind !== "note" || at.event.pitches.length !== 1) return null;

  const pitch = at.event.pitches[0];
  if (pitch === undefined || pitch.tab === null) return null;

  const fret = pitch.tab.fret * 10 + digit;
  if (pitch.tab.fret < 1 || fret > HIGHEST_FRET) return null;

  const { key } = resolveAttributes(score, at.measureIndex);
  const sounded = pitchAt(tuning, pitch.tab.string, fret, key.fifths);
  if (sounded === null) return null;

  return {
    command: {
      kind: "setPitches",
      eventId,
      pitches: [{ ...sounded, tieStart: pitch.tieStart, tieStop: pitch.tieStop }],
    },
    fret,
  };
}

/**
 * A fret typed on a tab staff: the note that string stops there, written at the caret.
 *
 * The string is the one the state is on, which the up and down arrows move, so a bar of
 * tablature is typed the way it is read -- a hand on a string, a run of numbers along it.
 * A staff with no strings refuses, because a fret means nothing on one.
 */
export function writeFret(score: Score, state: EntryState, fret: number): EntryResult {
  const tuning = tuningAt(score, state.caret?.partId ?? null);
  if (tuning === null) return refuse(state, "this staff has no strings to fret");
  if (state.caret === null) return refuse(state, "there is nowhere to write: click a bar first");

  if (state.fretting !== null && state.fretting === state.last && fret < 10) {
    const joined = extendFret(score, tuning, state.fretting, fret);
    if (joined !== null) {
      return { state, commands: [joined.command], refusal: null };
    }
  }

  const measure = measureIndexOf(score, state.caret.measureId);
  const fifths = measure === null ? 0 : resolveAttributes(score, measure).key.fifths;
  const pitch = pitchAt(tuning, state.string, fret, fifths);
  if (pitch === null) return refuse(state, `there is no fret ${fret} on string ${state.string}`);

  const result = write(score, state, makeNote([{ ...pitch, tieStop: state.tie }], state.duration));
  if (result.refusal !== null) return result;

  const back =
    state.tie && state.last !== null ? tieBack(score, state.last, pitch.step, pitch.octave) : null;
  return {
    ...result,
    commands: back === null ? result.commands : [...result.commands, back],
    state: {
      ...result.state,
      octave: pitch.octave,
      step: pitch.step,
      fretting: result.state.last,
    },
  };
}

/** Move to another string, stopping at the outside ones rather than wrapping round. */
export function moveString(score: Score, state: EntryState, delta: number): EntryResult {
  const tuning = tuningAt(score, state.caret?.partId ?? null);
  if (tuning === null) return refuse(state, "this staff has no strings to move between");
  const string = Math.max(1, Math.min(tuning.length, state.string + delta));
  return { state: { ...state, string }, commands: [], refusal: null };
}
